import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import ValetApi from "../api/Api";
import "./PodiumCapacityConfig.css";

const PodiumCapacityConfig = () => {
    const [podiums, setPodiums] = useState([]);
    const [locations, setLocations] = useState([]);
    const [draftCapacities, setDraftCapacities] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingByPodiumId, setSavingByPodiumId] = useState({});

    const locationNameById = useMemo(() => {
        const map = new Map();
        locations.forEach((loc) => map.set(Number(loc.id), loc.name));
        return map;
    }, [locations]);

    async function loadData() {
        try {
            setLoading(true);
            const [podiumsRes, locationsRes] = await Promise.all([ValetApi.getAllPodiums(), ValetApi.getAllLocations()]);
            const nextPodiums = Array.isArray(podiumsRes?.data?.podiums) ? podiumsRes.data.podiums : [];
            const nextLocations = Array.isArray(locationsRes?.data?.locations) ? locationsRes.data.locations : [];
            setPodiums(nextPodiums);
            setLocations(nextLocations);

            const nextDrafts = {};
            nextPodiums.forEach((podium) => {
                nextDrafts[podium.id] = podium?.parkingCapacity == null ? "" : String(podium.parkingCapacity);
            });
            setDraftCapacities(nextDrafts);
        } catch {
            toast.error("Unable to load podium capacity settings.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    async function handleSaveCapacity(podium) {
        const podiumId = Number(podium.id);
        const rawValue = String(draftCapacities[podiumId] ?? "").trim();

        if (rawValue !== "") {
            const parsed = Number(rawValue);
            if (!Number.isInteger(parsed) || parsed < 0) {
                toast.error("Capacity must be a whole number 0 or greater.");
                return;
            }
        }

        const payload = {
            parkingCapacity: rawValue === "" ? null : Number(rawValue),
        };

        try {
            setSavingByPodiumId((prev) => ({ ...prev, [podiumId]: true }));
            await ValetApi.updatePodium(podiumId, payload);
            toast.success(`Saved capacity for ${podium.name}.`, { autoClose: 1200 });
            await loadData();
        } catch {
            toast.error(`Unable to save capacity for ${podium.name}.`);
        } finally {
            setSavingByPodiumId((prev) => ({ ...prev, [podiumId]: false }));
        }
    }

    if (loading) {
        return (
            <div className="podium-capacity-loading">
                <Spinner size="sm" /> Loading podium capacity settings...
            </div>
        );
    }

    return (
        <div className="podium-capacity-config">
            <div className="podium-capacity-note">Set the available parking spaces for each podium to track lot fullness in Insights.</div>

            <div className="podium-capacity-grid">
                <div className="podium-capacity-grid-header">Podium</div>
                <div className="podium-capacity-grid-header">Location</div>
                <div className="podium-capacity-grid-header">Capacity</div>
                <div className="podium-capacity-grid-header">Action</div>

                {podiums.map((podium) => {
                    const podiumId = Number(podium.id);
                    const isSaving = Boolean(savingByPodiumId[podiumId]);
                    return (
                        <React.Fragment key={podiumId}>
                            <div className="podium-capacity-cell podium-capacity-name">{podium.name}</div>
                            <div className="podium-capacity-cell">{locationNameById.get(Number(podium.locationId)) || `Location ${podium.locationId}`}</div>
                            <div className="podium-capacity-cell">
                                <Input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={draftCapacities[podiumId] ?? ""}
                                    onChange={(evt) => {
                                        const nextValue = evt.target.value;
                                        setDraftCapacities((prev) => ({ ...prev, [podiumId]: nextValue }));
                                    }}
                                    placeholder="Not set"
                                    className="podium-capacity-input"
                                />
                            </div>
                            <div className="podium-capacity-cell">
                                <Button
                                    color="primary"
                                    size="sm"
                                    className="podium-capacity-save-btn"
                                    onClick={() => handleSaveCapacity(podium)}
                                    disabled={isSaving}>
                                    {isSaving ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default PodiumCapacityConfig;
